import { ROLE_LABELS } from '@fos/shared/labels';
import { Lock, LogOut, Settings } from 'lucide-react';
import { useNavigate } from 'react-router';
import { errorMessage } from '../../lib/api';
import { type ActiveSession, useLogout, useSwitchWorkspace } from '../../lib/auth';
import { Badge } from '../../ui/Badge';
import { MenuItem, MenuLabel, MenuRadioGroup, MenuRadioItem, MenuSeparator } from '../../ui/Menu';
import { toast } from '../../ui/Toast';
import { useMyWorkspaces, useOtherWorkspaces } from './other-workspaces';

/**
 * Wylogowanie. `mutateAsync` (a nie callbacki `mutate`), bo menu, z którego wywołujemy akcję,
 * zamyka się i odmontowuje, zanim żądanie się skończy — callbacki `mutate` by przepadły.
 */
export function useSignOut(): () => void {
  const logout = useLogout();
  const navigate = useNavigate();
  return () => {
    void logout
      .mutateAsync()
      .catch(() => undefined)
      .finally(() => navigate('/login', { replace: true }));
  };
}

/** Zawartość menu workspace'u: lista workspace'ów (przełączanie) i wylogowanie. */
export function WorkspaceMenuItems({ session, onNavigate }: { session: ActiveSession; onNavigate?: () => void }) {
  const workspaces = useMyWorkspaces();
  // 8.10: liczba rzeczy czekających w innych workspace'ach (te same dane co plakietka przy nazwie i sekcja na Dziś)
  const waiting = new Map(useOtherWorkspaces().data.workspaces.map((w) => [w.workspace.id, w.waiting]));
  const switchWorkspace = useSwitchWorkspace();
  const signOut = useSignOut();
  const navigate = useNavigate();

  const items =
    workspaces.data ?? (session.workspace ? [{ ...session.workspace, role: session.membership?.role ?? 'user' }] : []);

  return (
    <>
      <MenuLabel>Workspace</MenuLabel>
      <MenuRadioGroup
        value={session.workspace?.id ?? ''}
        onValueChange={(workspaceId) => {
          if (workspaceId === session.workspace?.id) return;
          switchWorkspace.mutateAsync(workspaceId).then(
            () => {
              navigate('/dzis');
              onNavigate?.();
            },
            (err: unknown) => toast.error(errorMessage(err)),
          );
        }}
      >
        {items.map((ws) => {
          const n = waiting.get(ws.id) ?? 0;
          return (
            <MenuRadioItem key={ws.id} value={ws.id}>
              <span className="flex min-w-0 flex-1 items-center gap-2">
                <span className="flex min-w-0 flex-1 flex-col">
                  <span className="truncate">{ws.name}</span>
                  <span className="text-caption font-normal text-fg-muted">
                    {'partner' in ws && ws.partner ? 'Partner' : ROLE_LABELS[ws.role]}
                    {'primary' in ws && ws.primary ? ' · główny' : ''}
                  </span>
                </span>
                {n > 0 && (
                  <>
                    <Badge kind="attention" count={n} />
                    <span className="sr-only">{`, czeka: ${n}`}</span>
                  </>
                )}
              </span>
            </MenuRadioItem>
          );
        })}
      </MenuRadioGroup>
      <MenuSeparator />
      <MenuItem icon={LogOut} onSelect={signOut}>
        Wyloguj
      </MenuItem>
    </>
  );
}

/** Zawartość menu konta (avatar w menu bocznym). */
export function UserMenuItems({ session, onNavigate }: { session: ActiveSession; onNavigate?: () => void }) {
  const navigate = useNavigate();
  const signOut = useSignOut();
  const go = (to: string) => {
    navigate(to);
    onNavigate?.();
  };
  return (
    <>
      <div className="px-2.5 pt-1.5 pb-2">
        <p className="truncate text-body-s font-medium text-fg">{session.user.name}</p>
        <p className="truncate text-caption text-fg-muted">{session.user.email}</p>
      </div>
      <MenuSeparator />
      <MenuItem icon={Settings} onSelect={() => go('/ustawienia/profil')}>
        Ustawienia
      </MenuItem>
      <MenuItem icon={Lock} onSelect={() => go('/ustawienia/bezpieczenstwo')}>
        Hasło i urządzenia
      </MenuItem>
      <MenuSeparator />
      <MenuItem icon={LogOut} onSelect={signOut}>
        Wyloguj
      </MenuItem>
    </>
  );
}
